<template>
  <b-container>
    <!-- HEADER -->
    <b-row>
      <b-col cols="12">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h3 class="font-weight-bold">GGR Reports</h3>
            <small class="text-muted">
              Gross Gaming Revenue analysis and breakdowns
            </small>
          </div>

          <div class="d-flex align-items-center">
            <b-form-select
              v-model="range"
              :options="rangeOptions"
              size="sm"
              class="mr-1"
            />
            <b-button variant="primary" class="text-nowrap" size="sm">
              <feather-icon icon="DownloadIcon" />
              Export
            </b-button>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row class="mt-1">
      <b-col cols="12">
        <b-row>
          <b-col cols="3">
            <b-card
              class="h-100"
              body-class="p-1"
              style="
                border-left: 4px solid rgb(59, 130, 246);
                background: linear-gradient(
                  rgb(239, 246, 255),
                  rgb(255, 255, 255)
                );
              "
            >
              <span class="text-muted"> Total GGR (MTD) </span>
              <h3 class="font-weight-bolder">$1.24M</h3>
              <div>
                <feather-icon icon="ArrowUpIcon" class="text-success" />
                <span class="small text-success"> 8.5% vs last month</span>
              </div>
            </b-card>
          </b-col>
          <b-col cols="3">
            <b-card
              class="h-100"
              body-class="p-1"
              style="
                border-left: 4px solid rgb(16, 185, 129);
                background: linear-gradient(
                  rgb(236, 253, 245),
                  rgb(255, 255, 255)
                );
              "
            >
              <span class="mb-1 text-muted"> Racing GGR </span>
              <h3 class="font-weight-bolder">$682K</h3>
              <span class="small text-muted"> 8.5% vs last month </span>
            </b-card>
          </b-col>
          <b-col cols="3">
            <b-card
              class="h-100"
              body-class="p-1"
              style="
                border-left: 4px solid rgb(239, 68, 68);
                background: linear-gradient(
                  rgb(254, 242, 242),
                  rgb(255, 255, 255)
                );
              "
            >
              <span class="mb-1 text-muted">Sports GGR</span>
              <h3 class="font-weight-bolder">$558K</h3>
              <span class="small text-muted"> 45% of total</span>
            </b-card>
          </b-col>
          <b-col cols="3">
            <b-card
              class="h-100"
              body-class="p-1"
              style="
                border-left: 4px solid rgb(132, 204, 22);
                background: linear-gradient(
                  rgb(247, 254, 231),
                  rgb(255, 255, 255)
                );
              "
            >
              <span class="mb-1 text-muted">GGR Margin</span>
              <h3 class="font-weight-bolder">9.2%</h3>
              <div>
                <feather-icon icon="ArrowUpIcon" class="text-success" />
                <span class="small text-success"> 0.3pp</span>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-row class="mt-2">
      <b-col cols="8">
        <b-card class="shadow-sm">
          <h5 class="mb-2 font-weight-bolder">GGR by Category</h5>
          <b-table
            :items="categories"
            :fields="fields"
            small
            responsive
            class="mb-0"
          >
            <!-- Turnover -->
            <template #cell(turnover)="data">
              <strong>{{ data.item.turnover }}</strong>
            </template>

            <!-- GGR -->
            <template #cell(ggr)="data">
              <strong>{{ data.item.ggr }}</strong>
            </template>

            <!-- Margin -->
            <template #cell(margin)="data">
              {{ data.item.margin }}
            </template>

            <!-- Trend -->
            <template #cell(trend)="data">
              <feather-icon
                v-if="data.item.trend === 'up'"
                icon="ArrowUpIcon"
                class="text-success"
              />
              <feather-icon
                v-else-if="data.item.trend === 'down'"
                icon="ArrowDownIcon"
                class="text-danger"
              />
              <span v-else class="text-muted"> — </span>
            </template>
          </b-table>
        </b-card>
      </b-col>
      <b-col cols="4">
        <b-card class="shadow-sm">
          <h5 class="font-weight-bolder">GGR Reports</h5>

          <div
            v-for="(file, index) in reports"
            :key="index"
            class="report-item"
          >
            <div class="d-flex align-items-center">
              <div class="excel-icon mr-1">
                <feather-icon icon="FileTextIcon" size="16" />
              </div>

              <div class="flex-grow-1">
                <div class="font-weight-semibold">
                  {{ file.name }}
                </div>
                <small class="text-muted">
                  {{ file.status }}
                </small>
              </div>

              <b-button size="sm" variant="primary">
                <feather-icon icon="DownloadIcon" size="14" />
              </b-button>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import {
  BContainer,
  BRow,
  BCol,
  BCard,
  BFormSelect,
  BButton,
  BLink,
  BTable,
} from "bootstrap-vue";
import FeatherIcon from "@/@core/components/feather-icon/FeatherIcon.vue";
export default {
  components: {
    BContainer,
    BRow,
    BCol,
    BCard,
    BFormSelect,
    BButton,
    BLink,
    BTable,
    FeatherIcon,
  },
  data() {
    const rangeOptions = [
      { value: "7days", text: "Last 7 days" },
      { value: "30days", text: "Last 30 days" },
      { value: "this_month", text: "This Month" },
      { value: "last_quarter", text: "Last Quarter" },
    ];
    return {
      range: rangeOptions[0].value,
      rangeOptions,
      fields: [
        { key: "category", label: "Category" },
        { key: "turnover", label: "Turnover" },
        { key: "ggr", label: "GGR" },
        { key: "margin", label: "Margin" },
        { key: "trend", label: "Trend" },
      ],

      categories: [
        {
          category: "Thoroughbred Racing",
          turnover: "$4.2M",
          ggr: "$378K",
          margin: "9.0%",
          trend: "up",
        },
        {
          category: "Harness Racing",
          turnover: "$1.8M",
          ggr: "$162K",
          margin: "9.0%",
          trend: "up",
        },
        {
          category: "Greyhound Racing",
          turnover: "$1.6M",
          ggr: "$142K",
          margin: "8.9%",
          trend: "neutral",
        },
        {
          category: "AFL",
          turnover: "$2.1M",
          ggr: "$189K",
          margin: "9.0%",
          trend: "up",
        },
        {
          category: "NRL",
          turnover: "$1.4M",
          ggr: "$126K",
          margin: "9.0%",
          trend: "up",
        },
        {
          category: "Soccer",
          turnover: "$1.2M",
          ggr: "$108K",
          margin: "9.0%",
          trend: "down",
        },
        {
          category: "Other Sports",
          turnover: "$1.5M",
          ggr: "$135K",
          margin: "9.0%",
          trend: "up",
        },
      ],

      reports: [
        {
          name: "ggr_january_2026.xlsx",
          status: "Updated: Today",
        },
        {
          name: "ggr_december_2025.xlsx",
          status: "Final",
        },
        {
          name: "ggr_q4_2025.xlsx",
          status: "Final",
        },
      ],
    };
  },
  methods: {},
};
</script>

<style lang="scss" scoped>
.report-item {
  padding: 12px 0;
  border-bottom: 1px solid #f1f1f1;
}

.report-item:last-child {
  border-bottom: none;
}

.excel-icon {
  width: 36px;
  height: 36px;
  background: #e6f4ea;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>